import * as React from 'react'

import { animate, motion, useMotionValue, useTransform } from 'motion/react'

import * as Progress from '@radix-ui/react-progress'
import { Badge, Flex, Text } from '@buffer-mono/popcorn'

import { getScoreColors } from '../../util/commentScoreTheme'
import styles from './CommentScoreMetric.module.css'

export const CommentScoreMetric = ({
  level,
  score,
}: {
  level: string
  score: number
}): JSX.Element => {
  const progress = useMotionValue(0)
  const roundedProgress = useTransform(() => Math.round(progress.get()))
  const pathLength = useTransform(progress, [0, 100], [0, 1])
  const filter = useTransform(progress, [0, score], ['blur(10px)', 'blur(0px)'])
  const scale = useTransform(progress, [0, score], [0.5, 1])
  const opacity = useTransform(progress, [0, score], [0.5, 1])

  const maxDuration = 2 // seconds for 100
  const minDuration = 0.5 // minimum duration for very low values
  const duration = Math.max((score / 100) * maxDuration, minDuration)
  const colors = getScoreColors(score)

  React.useEffect(() => {
    if (score === 0) {
      progress.set(0) // No arc, just the dot
    } else {
      animate(progress, score, {
        duration,
        ease: [0.31, 0.05, 0.28, 0.85],
      })
    }
  }, [progress, duration, score])

  return (
    <Flex direction="column" align="center" gap="xs">
      <Badge
        className={styles.scoreBadge}
        size="medium"
        style={{
          backgroundColor: colors.containerBg,
          color: colors.textColor,
          borderColor: colors.progressColor,
        }}
      >
        {level}
      </Badge>
      <div className={styles.container}>
        <Progress.Root className={styles.root}>
          <Progress.Indicator asChild>
            <div className={styles.indicatorContainer}>
              <svg className={styles.indicator} viewBox="0 0 100 100">
                {/* Base arc (background) */}
                <path
                  d="M22.93 77.07 A40 40 0 1 1 77.07 77.07"
                  fill="none"
                  stroke={colors.containerBg}
                  strokeWidth="9"
                  strokeLinecap="round"
                />
                {/* Progress arc (foreground, animated) or left dot for 0 */}
                {score === 0 ? (
                  <circle
                    cx={22.93}
                    cy={77.07}
                    r={4.5}
                    fill={colors.progressColor}
                  />
                ) : (
                  <motion.path
                    d="M22.93 77.07 A40 40 0 1 1 77.07 77.07"
                    fill="none"
                    stroke={colors.progressColor}
                    strokeWidth="9"
                    style={{
                      pathLength,
                      strokeLinecap: 'round',
                    }}
                  />
                )}
              </svg>
              <div className={styles.inputContainer}>
                <div
                  className={styles.inputWrapper}
                  style={{
                    backgroundColor: colors.containerBg,
                  }}
                >
                  <motion.div
                    className={styles.input}
                    style={{
                      filter,
                      scale,
                      opacity,
                      color: colors.textColor,
                      willChange: 'filter, transform, opacity',
                    }}
                  >
                    {roundedProgress}
                  </motion.div>
                </div>
                <Text size="sm" color="subtle" weight="medium">
                  /100
                </Text>
              </div>
            </div>
          </Progress.Indicator>
        </Progress.Root>
      </div>
    </Flex>
  )
}
